import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  FileText,
  MoreHorizontal,
  Ban,
  Eye,
  Filter,
  X,
  AlertCircle,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Hash,
  DollarSign,
  CircleCheck,
  Clock,
  FileDown,
  Paperclip,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { useIncomingInvoicesList, useAnnulIncomingInvoice, useUpdateIncomingInvoiceStatus } from '../hooks/useIncomingInvoices'
import { toast } from '../hooks/useToast'
import { incomingInvoiceApi, GetIncomingInvoicesParams } from '../services/incomingInvoices'
import { IncomingInvoiceStatus } from '../types/incomingInvoice'
import { getApiError } from '../lib/api'

const statusKeys: Record<IncomingInvoiceStatus, string> = {
  draft: 'incomingInvoices.status.draft',
  created: 'incomingInvoices.status.created',
  annulled: 'incomingInvoices.status.annulled',
  corrected: 'incomingInvoices.status.corrected',
  paid: 'incomingInvoices.status.paid',
  partially_paid: 'incomingInvoices.status.partiallyPaid',
  pending_processing: 'incomingInvoices.status.pendingProcessing',
}

const statusVariants: Record<IncomingInvoiceStatus, 'secondary' | 'warning' | 'success' | 'destructive' | 'default'> = {
  draft: 'secondary',
  created: 'warning',
  annulled: 'destructive',
  corrected: 'default',
  paid: 'success',
  partially_paid: 'warning',
  pending_processing: 'secondary',
}

const PAGE_SIZE = 10

// Debounce hook
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(handler)
    }
  }, [value, delay])

  return debouncedValue
}

export default function IncomingInvoices() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const annulMutation = useAnnulIncomingInvoice()
  const updateStatusMutation = useUpdateIncomingInvoiceStatus()

  // Filters
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [showFilters, setShowFilters] = useState(false)

  // Pagination
  const [page, setPage] = useState(0)

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(searchQuery, 300)

  // Build query params for server-side filtering
  const queryParams = useMemo((): GetIncomingInvoicesParams => {
    const params: GetIncomingInvoicesParams = {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter as IncomingInvoiceStatus
    }
    if (debouncedSearch) {
      params.search = debouncedSearch
    }
    if (dateFrom) {
      params.dateFrom = dateFrom
    }
    if (dateTo) {
      params.dateTo = dateTo
    }
    return params
  }, [statusFilter, debouncedSearch, dateFrom, dateTo, page])

  const { data: invoices = [], isLoading, isFetching, error, refetch } = useIncomingInvoicesList(queryParams)

  const clearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
    setDateFrom('')
    setDateTo('')
    setPage(0)
  }

  const hasActiveFilters = searchQuery || statusFilter !== 'all' || dateFrom || dateTo

  const handleFilterChange = () => {
    setPage(0) // Reset to first page when any filter changes
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
    handleFilterChange()
  }

  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    handleFilterChange()
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    handleFilterChange()
  }

  const handleStatusChange = async (id: number, status: IncomingInvoiceStatus) => {
    try {
      await updateStatusMutation.mutateAsync({ id, status })
      toast({
        title: t('incomingInvoices.toast.statusUpdated'),
        description: t('incomingInvoices.toast.statusUpdatedDesc', { status: t(statusKeys[status]) }),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleAnnul = async (id: number) => {
    if (!window.confirm(t('incomingInvoices.actions.confirmAnnul'))) {
      return
    }

    try {
      await annulMutation.mutateAsync(id)
      toast({
        title: t('incomingInvoices.toast.annulled'),
        description: t('incomingInvoices.toast.annulledDesc'),
      })
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const handleExportCsv = async () => {
    try {
      const blob = await incomingInvoiceApi.exportCsv({
        status: statusFilter !== 'all' ? statusFilter as IncomingInvoiceStatus : undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'incoming-invoices.csv'
      a.click()
      URL.revokeObjectURL(url)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  // Summary stats
  const stats = useMemo(() => {
    const total = invoices.reduce((sum, inv) => sum + inv.totalAmount, 0)
    const paid = invoices
      .filter(inv => inv.status === 'paid')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)
    const pending = invoices
      .filter(inv => inv.status === 'created' || inv.status === 'partially_paid' || inv.status === 'pending_processing')
      .reduce((sum, inv) => sum + inv.totalAmount, 0)
    return { total, paid, pending, count: invoices.length }
  }, [invoices])

  // Pagination info
  const hasNextPage = invoices.length === PAGE_SIZE
  const hasPrevPage = page > 0

  // Error state
  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('incomingInvoices.errorLoading')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('incomingInvoices.title')}</h1>
          <p className="text-muted-foreground">
            {t('incomingInvoices.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportCsv}>
            <FileDown className="mr-2 h-4 w-4" />
            {t('incomingInvoices.exportCsv')}
          </Button>
          <Button onClick={() => navigate('/dashboard/incoming-invoices/create')}>
            <Plus className="mr-2 h-4 w-4" />
            {t('incomingInvoices.newInvoice')}
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-20" />
                </CardHeader>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardDescription>{t('incomingInvoices.stats.totalInvoices')}</CardDescription>
                <Hash className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl">{stats.count}</CardTitle>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardDescription>{t('incomingInvoices.stats.totalAmount')}</CardDescription>
                <DollarSign className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl">{formatCurrency(stats.total)}</CardTitle>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardDescription>{t('incomingInvoices.stats.paid')}</CardDescription>
                <CircleCheck className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl text-green-600">{formatCurrency(stats.paid)}</CardTitle>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardDescription>{t('incomingInvoices.stats.pending')}</CardDescription>
                <Clock className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl text-yellow-600">{formatCurrency(stats.pending)}</CardTitle>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            {/* Search and filter toggle */}
            <div className="flex flex-col gap-2 sm:flex-row">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder={t('incomingInvoices.searchPlaceholder')}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex gap-2">
                <Button
                  variant={showFilters ? 'secondary' : 'outline'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <Filter className="mr-2 h-4 w-4" />
                  {t('common.filters')}
                  {hasActiveFilters && (
                    <Badge variant="default" className="ml-2">
                      {[statusFilter !== 'all', dateFrom, dateTo].filter(Boolean).length}
                    </Badge>
                  )}
                </Button>
                {hasActiveFilters && (
                  <Button variant="ghost" size="icon" onClick={clearFilters}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Extended filters */}
            {showFilters && (
              <div className="grid gap-4 sm:grid-cols-3 pt-4 border-t">
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('common.status')}</label>
                  <Select
                    value={statusFilter}
                    onChange={e => handleStatusFilterChange(e.target.value)}
                  >
                    <option value="all">{t('incomingInvoices.status.all')}</option>
                    <option value="draft">{t('incomingInvoices.status.draft')}</option>
                    <option value="created">{t('incomingInvoices.status.created')}</option>
                    <option value="paid">{t('incomingInvoices.status.paid')}</option>
                    <option value="partially_paid">{t('incomingInvoices.status.partiallyPaid')}</option>
                    <option value="pending_processing">{t('incomingInvoices.status.pendingProcessing')}</option>
                    <option value="annulled">{t('incomingInvoices.status.annulled')}</option>
                    <option value="corrected">{t('incomingInvoices.status.corrected')}</option>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('common.from')}</label>
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={e => handleDateFromChange(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">{t('common.to')}</label>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={e => handleDateToChange(e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Incoming Invoices Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 w-28" />
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-12 flex-1" />
                  <Skeleton className="h-12 w-28" />
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-12 w-12" />
                </div>
              ))}
            </div>
          ) : invoices.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold">{t('incomingInvoices.noInvoices')}</h3>
              <p className="text-muted-foreground mb-4">
                {hasActiveFilters
                  ? t('incomingInvoices.noInvoicesFiltered')
                  : t('incomingInvoices.createFirst')}
              </p>
              {!hasActiveFilters && (
                <Button onClick={() => navigate('/dashboard/incoming-invoices/create')}>
                  <Plus className="mr-2 h-4 w-4" />
                  {t('incomingInvoices.newInvoice')}
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="relative">
                {/* Loading overlay for refetch */}
                {isFetching && !isLoading && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('incomingInvoices.table.number')}</TableHead>
                      <TableHead>{t('incomingInvoices.table.date')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('incomingInvoices.table.supplier')}</TableHead>
                      <TableHead className="hidden lg:table-cell">{t('incomingInvoices.table.nif')}</TableHead>
                      <TableHead>{t('incomingInvoices.table.amount')}</TableHead>
                      <TableHead>{t('incomingInvoices.table.files')}</TableHead>
                      <TableHead>{t('incomingInvoices.table.status')}</TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map(invoice => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.invoiceNumber}</TableCell>
                        <TableCell>{formatDate(invoice.date)}</TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="max-w-[200px] truncate">{invoice.supplier.name}</div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">{invoice.supplier.taxId || '-'}</TableCell>
                        <TableCell className="font-medium">
                          {formatCurrency(invoice.totalAmount)}
                        </TableCell>
                        <TableCell>
                          {invoice.files.length > 0 && (
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Paperclip className="h-3.5 w-3.5" />
                              <span className="text-xs">{invoice.files.length}</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusVariants[invoice.status] as any}>
                            {t(statusKeys[invoice.status])}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Actions</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  navigate(`/dashboard/incoming-invoices/${invoice.id}`)
                                }
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                {t('incomingInvoices.actions.viewDetails')}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {/* Status actions */}
                              {invoice.status === 'draft' && (
                                <DropdownMenuItem
                                  onClick={() => handleStatusChange(invoice.id, 'created')}
                                >
                                  {t('incomingInvoices.actions.markAsCreated')}
                                </DropdownMenuItem>
                              )}
                              {invoice.status === 'created' && (
                                <>
                                  <DropdownMenuItem
                                    onClick={() => handleStatusChange(invoice.id, 'paid')}
                                  >
                                    {t('incomingInvoices.actions.markAsPaid')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleStatusChange(invoice.id, 'partially_paid')}
                                  >
                                    {t('incomingInvoices.actions.markAsPartiallyPaid')}
                                  </DropdownMenuItem>
                                </>
                              )}
                              {/* Annul action for draft/created */}
                              {(invoice.status === 'draft' || invoice.status === 'created') && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => handleAnnul(invoice.id)}
                                  >
                                    <Ban className="mr-2 h-4 w-4" />
                                    {t('incomingInvoices.actions.annul')}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {t('common.page')} {page + 1} {hasActiveFilters && t('incomingInvoices.filtered')}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p - 1)}
                    disabled={!hasPrevPage || isFetching}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => p + 1)}
                    disabled={!hasNextPage || isFetching}
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
